// frontend/src/components/SecondChoiceForm.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  FileText, 
  Info, 
  AlertCircle, 
  Save, 
  GripVertical,
  CheckCircle2,
  HelpCircle
} from 'lucide-react';
import { getSecondChoiceData, saveSecondPreferences, updateUserFlag } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import OnboardingTour from './OnboardingTour';
import '../styles/Dashboard.css';
import '../styles/SecondChoice.css';

// チュートリアルのステップ定義
const TOUR_STEPS = [
  {
    target: '[data-tour="second-choice-form"]',
    title: '2次希望登録へようこそ！',
    content: 'このページでは、1次抽選の結果から、希望する配属案の優先順位を設定します。',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="second-choice-info"]',
    title: '重要なお知らせ',
    content: '通常は「結果5（線形計画法）」が採用されます。この登録はバックアップとして使用されます。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="second-choice-list"]',
    title: '配属案リスト',
    content: 'ここに各配属案が表示されています。上にある配属案ほど優先度が高くなります。',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="second-choice-item"]',
    title: 'ドラッグで並び替え',
    content: 'この部分をドラッグ＆ドロップして、希望する順番に並び替えてください。一番上が第1希望になります。',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
  },
  {
    target: '[data-tour="second-choice-save"]',
    title: '保存を忘れずに！',
    content: '並び替えが完了したら、必ず「順位を保存する」ボタンを押して登録を完了してください。',
    placement: 'top',
    disableBeacon: true,
  },
];

const SECOND_CHOICE_SKIPPED = true;

function SecondChoiceForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [choices, setChoices] = useState([]);
  const [order, setOrder] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (SECOND_CHOICE_SKIPPED) {
      setIsLoading(false);
      return;
    }
    fetchSecondChoices();
  }, [user, navigate]);

  const fetchSecondChoices = async () => {
    try {
      const secondChoices = await getSecondChoiceData();
      if (secondChoices.length === 0) {
        setError('抽選前です。しばらくお待ちください。');
        setIsLoading(false);
        return;
      }
      const choicesWithStringId = secondChoices.map(choice => ({
        ...choice,
        id: choice.id.toString(),
      }));
      setChoices(choicesWithStringId);
      setOrder(choicesWithStringId.map(choice => choice.id));
    } catch (err) {
      console.error('Failed to fetch second choices:', err);
      setError('抽選前です。しばらくお待ちください。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const newOrder = Array.from(order);
    const [reorderedItem] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, reorderedItem);

    setOrder(newOrder);
    setIsSaved(false);
  };

  const handleSave = async () => {
    const confirmSave = window.confirm("現在の順序で希望を登録/更新しますか？");
    if (!confirmSave) return;
  
    setIsRegistering(true);
    try {
      const preferences = order.map((id, index) => ({
        lottery_model_id: parseInt(id, 10),
        preference_order: index + 1
      }));
      await saveSecondPreferences(preferences);
      // ユーザーのフラグを更新
      await updateUserFlag(user.uid, 'second_preference_status', true);
      
      setIsSaved(true);
      // 3秒後に保存完了メッセージを消す
      setTimeout(() => setIsSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('二次希望の保存に失敗しました。');
    } finally {
      setIsRegistering(false);
    }
  };

  if (isLoading) return (
    <div className="dashboard-loading">
      <LoadingSpinner text="データを読み込んでいます..." />
    </div>
  );

  if (SECOND_CHOICE_SKIPPED) return (
    <div className="dashboard second-choice second-choice-scope">
      <div className="dashboard-container">
        <div className="dashboard-grid">
          <div className="dashboard-card full-width second-choice-card-shell">
            <div className="card-header second-choice-header">
              <h2 className="card-title">
                <FileText size={24} className="icon-blue" />
                2次希望登録
              </h2>
            </div>
            <div className="notification-item info-notice">
              <div className="notification-meta">
                <Info size={16} />
                <span className="info-notice-title">お知らせ</span>
              </div>
              <h3 className="notification-title info-notice-heading">2次希望登録はスキップされました</h3>
              <p className="notification-body info-notice-body">
                今回は2次希望登録の手続きはありません。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="dashboard-error">
      <AlertCircle size={48} />
      <p>{error}</p>
    </div>
  );

  return (
    <div className="dashboard second-choice second-choice-scope">
      {/* オンボーディングツアー */}
      <OnboardingTour
        tourKey="second-choice"
        steps={TOUR_STEPS}
        isReady={!isLoading && choices.length > 0}
      />
      
      <div className="dashboard-container">
        <div className="dashboard-grid">
          
          {/* ヘッダーセクション */}
          <div className="dashboard-card full-width second-choice-card-shell" data-tour="second-choice-form">
            <div className="card-header second-choice-header">
              <h2 className="card-title">
                <FileText size={24} className="icon-blue" />
                2次希望登録フォーム
              </h2>
              <button
                type="button"
                className="tour-reset-button"
                onClick={() => {
                  try {
                    localStorage.removeItem('tour-completed-second-choice');
                  } catch {}
                  window['second-choiceTour']?.startTour?.();
                }}
                title="操作ガイドを再表示"
              >
                <HelpCircle size={18} />
                <span>操作ガイド</span>
              </button>
            </div>
            
            {/* 重要なお知らせ（Result 5について） */}
            <div className="notification-item info-notice" data-tour="second-choice-info">
              <div className="notification-meta">
                <Info size={16} />
                <span className="info-notice-title">この登録について</span>
              </div>
              <h3 className="notification-title info-notice-heading">原則として「結果5（線形計画法）」が採用されます</h3>
              <p className="notification-body info-notice-body">
                当システムでは、通常「結果5（線形計画法）」によって最適な配属先が決定されます。
                この2次希望登録は、万が一線形計画法で解が見つからなかった場合のバックアップとして使用されるものです。
                過去の実績ではほとんどの場合で結果5が採用されていますが、念のため希望順位の登録をお願いいたします。
              </p>
            </div>

            <p className="card-description">
              以下の配属案（結果1〜4など）を希望する順に並び替えてください。
              ドラッグ＆ドロップで順序を変更できます。
            </p>

            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="choices">
                {(provided) => (
                  <ul 
                    className="status-list second-choice-list" 
                    {...provided.droppableProps} 
                    ref={provided.innerRef}
                    data-tour="second-choice-list"
                  >
                    {order.map((id, index) => {
                      const choice = choices.find(c => c.id === id);
                      return (
                        <Draggable key={id} draggableId={id} index={index}>
                          {(provided, snapshot) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`second-choice-card ${snapshot.isDragging ? 'dragging' : ''}`}
                              style={{ ...provided.draggableProps.style }}
                              data-tour={index === 0 ? 'second-choice-item' : undefined}
                            >
                              {/* ヘッダー部分：順位と結果番号 */}
                              <div 
                                className={`second-choice-card-header ${index === 0 ? 'top-priority' : ''}`}
                              >
                                <div className="drag-handle" title="ドラッグして並び替え">
                                  <GripVertical size={24} />
                                </div>
                                
                                <div 
                                  className="priority-badge"
                                  style={{
                                    backgroundColor: index === 0 ? '#1e40af' : index === 1 ? '#3b82f6' : index === 2 ? '#60a5fa' : '#93c5fd',
                                    color: 'white'
                                  }}
                                >
                                  {index + 1}
                                </div>
                                
                                <div className="priority-title">
                                  <span className="priority-text">結果{choice.id}</span>
                                  {index === 0 && (
                                    <span className="priority-chip">第1希望</span>
                                  )}
                                </div>
                              </div>

                              {/* 配属一覧：テーブル形式で横並び */}
                              <div className="choice-table-wrapper">
                                <table className="choice-table">
                                  <thead>
                                    <tr>
                                      {[1, 2, 3, 4, 5, 6, 7].map((termNum) => (
                                        <th 
                                          key={termNum}
                                          className="choice-table-head"
                                        >
                                          Term {termNum}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      {choice.content.map((item, idx) => {
                                        const isUnassigned = item === '未割り当て';
                                        // 病院名と科名を分割
                                        const parts = item.split(' - ');
                                        const hospitalName = parts[0] || item;
                                        const departmentName = parts[1] || '';
                                        
                                        return (
                                          <td 
                                            key={idx}
                                            className={`choice-table-cell ${isUnassigned ? 'unassigned' : ''}`}
                                          >
                                            {isUnassigned ? (
                                              <span className="unassigned-text">未割り当て</span>
                                            ) : (
                                              <div className="choice-entry">
                                                <span className="choice-hospital">
                                                  {hospitalName}
                                                </span>
                                                {departmentName && (
                                                  <span className="choice-department">
                                                    {departmentName}
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </li>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>

            <div className="second-choice-actions" data-tour="second-choice-save">
              <button 
                onClick={handleSave} 
                disabled={isRegistering}
                className="second-choice-save-btn"
              >
                {isRegistering ? (
                  <>
                    <div className="loading-dot"></div>
                    登録中...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    順位を保存する
                  </>
                )}
              </button>
              
              {isSaved && (
                <div className="second-choice-saved-banner">
                  <CheckCircle2 size={18} />
                  <span>保存しました</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SecondChoiceForm;
